import React, { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUser } from "../../context/userContext";

const EditProductModal = ({
  isEditModalOpen,
  toggleEditModal,
  selectedProduct,
  setSelectedProduct,
  categories,
  subcategories,
  selectedCategory,
  setSelectedCategory,
  setSelectedSubcategory,
  handleInputChange,
  selectedSubcategory,
  handleEditProduct,
  fetchProducts,
}) => {
  const [categoryAttributes, setCategoryAttributes] = useState([]);
  const [subcategoryAttributes, setSubcategoryAttributes] = useState([]);
  const [categoryAttributeValues, setCategoryAttributeValues] = useState({});
  const [subcategoryAttributeValues, setSubcategoryAttributeValues] = useState(
    {}
  );
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imagesToRemove, setImagesToRemove] = useState([]);
  const { user } = useUser();

  const fetchAttributes = async (type, id) => {
    const urlMap = {
      category: `https://api.theonlysale.com/api/category-attributes/getByCategoryId/${id}`,
      subcategory: `https://api.theonlysale.com/api/subcategory-attributes/getBySubcategoryId/${id}`,
    };

    try {
      const response = await axios.get(urlMap[type]);
      if (response.status === 200) {
        if (type === "category") {
          setCategoryAttributes(response.data);
        } else {
          setSubcategoryAttributes(response.data);
        }
      } else {
        toast.error(`Failed to fetch ${type} attributes`);
      }
    } catch (error) {
      console.error(`Error fetching ${type} attributes:`, error);
      toast.error(`An error occurred while fetching ${type} attributes.`);
    }
  };

  const fetchAttributeValues = async (type, productId) => {
    const urlMap = {
      category: `https://api.theonlysale.com/api/product-category-attribute-values/getByProduct/${productId}`,
      subcategory: `https://api.theonlysale.com/api/product-subcategory-attribute-values/getByProduct/${productId}`,
    };

    try {
      const response = await axios.get(urlMap[type]);
      if (response.status === 200) {
        const values = {};
        response.data.forEach((attr) => {
          values[attr.attribute_id] = attr.value;
        });

        if (type === "category") {
          setCategoryAttributeValues(values);
        } else {
          setSubcategoryAttributeValues(values);
        }
      } else {
        console.error(`Failed to fetch ${type} attribute values`);
      }
    } catch (error) {
      console.error(`Error fetching ${type} attribute values:`, error);
    }
  };

  useEffect(() => {
    if (selectedProduct) {
      // Set selected category and subcategory IDs
      setSelectedCategory(selectedProduct.category_id);
      setSelectedSubcategory(selectedProduct.subcategory_id);
  
      // Fetch category attributes and values if category ID is present
      if (selectedProduct.category_id) {
        fetchAttributes("category", selectedProduct.category_id);
        fetchAttributeValues("category", selectedProduct.id);
      }
  
      // Fetch subcategory attributes and values if subcategory ID is present
      if (selectedProduct.subcategory_id) {
        fetchAttributes("subcategory", selectedProduct.subcategory_id);
        fetchAttributeValues("subcategory", selectedProduct.id);
      }
    }
  }, [selectedProduct, isEditModalOpen]); // <-- Add isEditModalOpen here
  
  const handleAttributeChange = (type, attributeId, value) => {
    if (type === "category") {
      setCategoryAttributeValues((prevValues) => ({
        ...prevValues,
        [attributeId]: value,
      }));
    } else {
      setSubcategoryAttributeValues((prevValues) => ({
        ...prevValues,
        [attributeId]: value,
      }));
    }
  };

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setImages((prevImages) => [...prevImages, ...files]);
  };

  const handleEditProductSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      console.log("Updating product with ID:", selectedProduct.id);
      const formData = new FormData();
      formData.append("name", selectedProduct.name);
      formData.append("details", selectedProduct.details);
      formData.append("price", selectedProduct.price);
      formData.append("discount", selectedProduct.discount);
      formData.append("category_id", selectedCategory);
      formData.append("subcategory_id", selectedSubcategory);
      formData.append("user_id", user.id);
      formData.append("stock", selectedProduct.stock);
      // Append category and subcategory attribute values
      for (const [attributeId, value] of Object.entries(
        categoryAttributeValues
      )) {
        formData.append(`categoryAttributes[${attributeId}]`, value);
      }
      for (const [attributeId, value] of Object.entries(
        subcategoryAttributeValues
      )) {
        formData.append(`subcategoryAttributes[${attributeId}]`, value);
      }

      // Append existing image URIs as strings
      selectedProduct.imageURIs.forEach((uri, index) => {
        formData.append(`existingImages[${index}]`, uri);
      });

      // Append images to remove
      imagesToRemove.forEach((uri, index) => {
        formData.append(`imagesToRemove[${index}]`, uri);
      });

      // Append new image files
      images.forEach((imageFile) => {
        formData.append("images", imageFile);
      });

      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`);
      }

      const response = await axios.put(
        `https://api.theonlysale.com/api/product/update/${selectedProduct.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        toast.success("Product updated successfully.");
        toggleEditModal();
        fetchProducts();
      }
    } catch (error) {
      console.error("Error editing product:", error);
      toast.error("An error occurred while editing the product.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = (index, type = "existing") => {
    if (type === "existing") {
      const updatedImages = selectedProduct.imageURIs.filter(
        (_, i) => i !== index
      );
      setSelectedProduct({
        ...selectedProduct,
        imageURIs: updatedImages,
      });
      setImagesToRemove((prev) => [...prev, selectedProduct.imageURIs[index]]);
    } else if (type === "new") {
      setImages((prevImages) => prevImages.filter((_, i) => i !== index));
    }
  };

  return (
    <>
      {isEditModalOpen && selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center w-full h-full overflow-y-auto bg-black bg-opacity-50">
          <div className="relative w-full max-w-2xl p-4 max-h-full">
            <div className="relative p-4 bg-white rounded-lg shadow dark:bg-gray-800 sm:p-5">
              <div className="flex justify-between items-center pb-4 mb-4 rounded-t border-b sm:mb-5 dark:border-gray-600">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Edit Product
                </h3>
                <button
                  type="button"
                  onClick={toggleEditModal}
                  className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white"
                >
                  <i className="fa fa-times w-5 h-5"></i>
                  <span className="sr-only">Close modal</span>
                </button>
              </div>
              <form onSubmit={handleEditProductSubmit}>
                <div className="grid gap-4 mb-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="edit-images"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Images
                    </label>
                    <div className="flex space-x-2 overflow-x-auto mb-4 relative">
                      {selectedProduct.imageURIs?.map((uri, index) => (
                        <div key={index} className="relative w-24 h-24">
                          <img
                            src={uri}
                            alt={`Product image ${index + 1}`}
                            className="w-full h-full object-cover rounded-lg shadow-md"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index, "existing")}
                            className="absolute top-0 right-0 p-1 bg-gray-500 text-white rounded-full hover:bg-gray-600"
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        </div>
                      ))}

                      {images.map((image, index) => (
                        <div key={index} className="relative w-24 h-24">
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`New image ${index + 1}`}
                            className="w-full h-full object-cover rounded-lg shadow-md"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index, "new")}
                            className="absolute top-0 right-0 p-1 bg-gray-500 text-white rounded-full hover:bg-gray-600"
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>

                    <input
                      type="file"
                      id="edit-image-files"
                      name="imageFiles"
                      multiple
                      onChange={handleImageChange}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="edit-name"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="edit-name"
                      value={selectedProduct.name}
                      onChange={handleInputChange}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      placeholder="Product name"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="edit-category"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Category
                    </label>
                    <select
                      id="edit-category"
                      name="category_id"
                      value={selectedCategory}
                      onChange={(e) =>
                        setSelectedCategory(parseInt(e.target.value))
                      }
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map((category) => (
                        <option
                          key={category.category_id}
                          value={category.category_id}
                        >
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="edit-subcategory"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Subcategory
                    </label>
                    <select
                      id="edit-subcategory"
                      name="subcategory_id"
                      value={selectedSubcategory}
                      onChange={(e) =>
                        setSelectedSubcategory(parseInt(e.target.value))
                      }
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      required
                    >
                      <option value="">Select Subcategory</option>
                      {subcategories.map((subcategory) => (
                        <option
                          key={subcategory.subcategory_id}
                          value={subcategory.subcategory_id}
                        >
                          {subcategory.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="edit-details"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Details
                    </label>
                    <textarea
                      id="edit-details"
                      name="details"
                      value={selectedProduct.details}
                      onChange={handleInputChange}
                      rows="4"
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      placeholder="Product details"
                      required
                    ></textarea>
                  </div>
                  <div>
                    <label
                      htmlFor="edit-price"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Price
                    </label>
                    <input
                      type="number"
                      name="price"
                      id="edit-price"
                      value={selectedProduct.price}
                      onChange={handleInputChange}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      placeholder="Product price"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="edit-discount"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Discount
                    </label>
                    <input
                      type="number"
                      name="discount"
                      id="edit-discount"
                      value={selectedProduct.discount}
                      onChange={handleInputChange}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      placeholder="Product discount"
                      required
                    />
                  </div>
                </div>
                <div>
                    <label
                      htmlFor="edit-name"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Stock
                    </label>
                    <input
                      type="number"
                      name="stock"
                      id="edit-stock"
                      value={selectedProduct.stock}
                      onChange={handleInputChange}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      placeholder="Product stock"
                      required
                    />
                  </div>
                {categoryAttributes.map((attribute) => {
                  const existingValue =
                    categoryAttributeValues[attribute.attribute_id] || "";

                  return (
                    <div key={attribute.attribute_id}>
                      <label
                        htmlFor={`edit-category-attribute-${attribute.attribute_id}`}
                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                      >
                        {attribute.name}
                      </label>
                      <input
                        type="text"
                        id={`edit-category-attribute-${attribute.attribute_id}`}
                        value={existingValue}
                        onChange={(e) =>
                          handleAttributeChange(
                            "category",
                            attribute.attribute_id,
                            e.target.value
                          )
                        }
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      />
                    </div>
                  );
                })}

                {subcategoryAttributes.map((attribute) => {
                  const existingValue =
                    subcategoryAttributeValues[attribute.attribute_id] || "";

                  return (
                    <div key={attribute.attribute_id}>
                      <label
                        htmlFor={`edit-subcategory-attribute-${attribute.attribute_id}`}
                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                      >
                        {attribute.name}
                      </label>
                      <input
                        type="text"
                        id={`edit-subcategory-attribute-${attribute.attribute_id}`}
                        value={existingValue}
                        onChange={(e) =>
                          handleAttributeChange(
                            "subcategory",
                            attribute.attribute_id,
                            e.target.value
                          )
                        }
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                      />
                    </div>
                  );
                })}

                <button
                  type="submit"
                  className={`${
                    loading
                      ? "bg-gray-500 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  } text-white mt-3 font-medium rounded-lg text-sm px-5 py-2.5 text-center`}
                >
                  {loading ? "Updating..." : "Update Product"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditProductModal;
